import React from 'react';

export type RelatedKeywordMetric = {
  keyword: string;
  search_volume: number | null;
  latest_monthly_search_volume: number | null;
  competition: string | null;
  competition_index: number | null;
  cpc: number | null;
  low_top_of_page_bid: number | null;
  high_top_of_page_bid: number | null;
};

export type RelatedKeywordsData = {
  keyword: string;
  location_code: number;
  language_code: string;
  search_partners: boolean;
  keywords: RelatedKeywordMetric[];
};

type Props = {
  data: RelatedKeywordsData | null;
  cached: boolean;
  loading: boolean;
  error: string | null;
  onFetch: () => void;
  onForceFetch?: () => void;
  onKeywordClick: (keyword: string) => void;
  onSaveKeyword?: (item: RelatedKeywordMetric) => void;
  isKeywordSaved?: (keyword: string) => boolean;
};

const formatNumber = (value: number | null | undefined) =>
  typeof value === 'number' ? value.toLocaleString('ja-JP') : '-';

const formatCurrency = (value: number | null | undefined) =>
  typeof value === 'number' ? `$${value.toFixed(2)}` : '-';

const getCompetitionClass = (competition: string | null) => {
  if (competition === 'HIGH') return 'bg-rose-50 text-rose-700 border-rose-100';
  if (competition === 'MEDIUM') return 'bg-amber-50 text-amber-700 border-amber-100';
  if (competition === 'LOW') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  return 'bg-slate-50 text-slate-500 border-slate-100';
};

const RelatedKeywordsCard: React.FC<Props> = ({
  data,
  cached,
  loading,
  error,
  onFetch,
  onForceFetch,
  onKeywordClick,
  onSaveKeyword,
  isKeywordSaved,
}) => {
  const keywords = data?.keywords || [];
  const visibleKeywords = keywords.slice(0, 100);
  const hiddenCount = Math.max(0, keywords.length - visibleKeywords.length);

  return (
    <section className="bg-white shadow-sm rounded-lg p-6 min-w-0">
      <div className="flex items-start justify-between border-b pb-2 mb-4 gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-bold text-gray-800">関連キーワード β</h2>
            {data && (
              <span className="text-[11px] font-semibold px-2 py-1 rounded bg-blue-50 text-blue-700">
                {formatNumber(keywords.length)}件
              </span>
            )}
            {cached && (
              <span className="text-[11px] font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-700">
                保存済みデータを表示中
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-gray-500">DataForSEO / Google Ads / 日本</p>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          {data && onForceFetch && (
            <button
              type="button"
              onClick={onForceFetch}
              disabled={loading}
              className="rounded border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              再取得
            </button>
          )}
          <button
            type="button"
            onClick={() => onFetch()}
            disabled={loading}
            className="bg-blue-500 text-white px-3 py-2 rounded text-sm font-semibold hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? '取得中...' : '関連キーワードを取得'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {error}
        </div>
      )}

      {!data ? (
        <div className="h-[180px] flex items-center justify-center text-center text-sm text-gray-500">
          ボタンを押すと広告データ由来の関連キーワードを取得します
        </div>
      ) : keywords.length === 0 ? (
        <div className="h-[180px] flex items-center justify-center text-center text-sm text-gray-500">
          関連キーワードは見つかりませんでした
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-gray-700 break-words">{data.keyword}</p>
            {hiddenCount > 0 && (
              <span className="text-[11px] text-gray-500">
                上位100件を表示中 / 残り{formatNumber(hiddenCount)}件
              </span>
            )}
          </div>

          <div className="overflow-x-auto rounded border border-gray-100">
            <table className="min-w-[860px] w-full text-sm">
              <thead className="bg-slate-50 text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold">キーワード</th>
                  <th className="px-3 py-3 text-right font-semibold">月間平均</th>
                  <th className="px-3 py-3 text-right font-semibold">直近月</th>
                  <th className="px-3 py-3 text-right font-semibold">CPC</th>
                  <th className="px-3 py-3 text-center font-semibold">競合</th>
                  <th className="px-3 py-3 text-right font-semibold">入札低</th>
                  <th className="px-3 py-3 text-right font-semibold">入札高</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleKeywords.map((item) => (
                  <tr
                    key={item.keyword}
                    onClick={() => onKeywordClick(item.keyword)}
                    className="cursor-pointer hover:bg-blue-50/70"
                  >
                    <td className="px-3 py-3 font-semibold text-gray-800">
                      <div className="flex items-center gap-2">
                        {onSaveKeyword && (
                          <button
                            type="button"
                            aria-label={`${item.keyword} を保存`}
                            title={isKeywordSaved?.(item.keyword) ? '保存済み' : '保存する'}
                            className={`shrink-0 rounded-full p-1 transition ${
                              isKeywordSaved?.(item.keyword)
                                ? 'text-amber-500 hover:bg-amber-50'
                                : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50'
                            }`}
                            onClick={(event) => {
                              event.stopPropagation();
                              onSaveKeyword(item);
                            }}
                          >
                            <i className={`${isKeywordSaved?.(item.keyword) ? 'fa-solid' : 'fa-regular'} fa-star`}></i>
                          </button>
                        )}
                        <button
                          type="button"
                          className="text-left hover:text-blue-700"
                          onClick={(event) => {
                            event.stopPropagation();
                            onKeywordClick(item.keyword);
                          }}
                        >
                          {item.keyword}
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right font-semibold text-gray-700">{formatNumber(item.search_volume)}</td>
                    <td className="px-3 py-3 text-right text-gray-600">{formatNumber(item.latest_monthly_search_volume)}</td>
                    <td className="px-3 py-3 text-right text-gray-600">{formatCurrency(item.cpc)}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex min-w-[72px] justify-center rounded border px-2 py-1 text-[11px] font-bold ${getCompetitionClass(item.competition)}`}>
                        {item.competition || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right text-gray-600">{formatCurrency(item.low_top_of_page_bid)}</td>
                    <td className="px-3 py-3 text-right text-gray-600">{formatCurrency(item.high_top_of_page_bid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};

export default RelatedKeywordsCard;
